<template>
  <section class="office-hero-section">
    <div class="hero-background">
      <img src="/images/office/office-hero-bg-new.jpg" alt="办公环境背景" class="background-image" />
      <div class="hero-overlay"></div>
    </div>

    <div class="hero-content">
      <div class="container">
        <div class="hero-text">
          <h1 class="hero-title">{{ $t('office.hero.title') }}</h1>
          <p class="hero-subtitle">{{ $t('office.hero.subtitle') }}</p>
        </div>

        <!-- 办公文档三件套 -->
        <div class="format-icons-section">
          <div class="format-icons-grid">
            <div
              v-for="format in formatIcons"
              :key="format.name"
              class="format-icon-item"
            >
              <Icon :name="format.icon" class="format-icon" />
              <span class="format-label">{{ format.label }}</span>
            </div>
          </div>
        </div>

        <!-- 特色服务标签 -->
        <div class="service-features">
          <div class="features-grid">
            <div
              v-for="feature in serviceFeatures"
              :key="feature.label"
              class="feature-item"
            >
              <Icon :name="feature.icon" class="feature-icon" />
              <span class="feature-label">{{ feature.label }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

// 办公文档三件套图标配置（i18n）
const formatIcons = computed(() => [
  { name: 'word', icon: 'vscode-icons:file-type-word', label: t('office.hero.icons.word') },
  { name: 'powerpoint', icon: 'vscode-icons:file-type-powerpoint', label: t('office.hero.icons.ppt') },
  { name: 'excel', icon: 'vscode-icons:file-type-excel', label: t('office.hero.icons.excel') }
])

// 特色服务标签配置（i18n）
const serviceFeatures = computed(() => [
  { icon: 'material-symbols:folder-open', label: t('office.hero.features.multi') },
  { icon: 'material-symbols:format-paint', label: t('office.hero.features.layout') },
  { icon: 'material-symbols:speed', label: t('office.hero.features.fast') },
  { icon: 'material-symbols:support-agent', label: t('office.hero.features.support') }
])
</script>

<style scoped>
.office-hero-section {
  position: relative;
  min-height: 600px;
  display: flex;
  align-items: center;
  color: white;
  overflow: hidden;
}

.hero-background {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: -2;
}

.background-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.hero-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, rgba(74, 144, 226, 0.8), rgba(80, 227, 194, 0.6));
  z-index: -1;
}

.hero-content {
  width: 100%;
  padding: 4rem 0;
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1rem;
}

.hero-text {
  text-align: center;
  margin-bottom: 3rem;
}

.hero-title {
  font-size: 3rem;
  font-weight: bold;
  margin-bottom: 1rem;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
}

.hero-subtitle {
  font-size: 1.25rem;
  opacity: 0.9;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
}

.format-icons-section {
  margin-bottom: 3rem;
}

.format-icons-grid {
  display: flex;
  justify-content: center;
  gap: 3rem;
  flex-wrap: wrap;
}

.format-icon-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

.format-icon {
  font-size: 3rem;
  margin-bottom: 0.5rem;
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.3));
}

.format-label {
  font-size: 1rem;
  font-weight: 600;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
}

.service-features {
  margin-top: 2rem;
}

.features-grid {
  display: flex;
  justify-content: center;
  gap: 2rem;
  flex-wrap: wrap;
}

.feature-item {
  display: flex;
  align-items: center;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  padding: 0.75rem 1.5rem;
  border-radius: 50px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  transition: all 0.3s ease;
}

.feature-item:hover {
  background: rgba(255, 255, 255, 0.2);
  transform: translateY(-2px);
}

.feature-icon {
  font-size: 1.25rem;
  margin-right: 0.5rem;
}

.feature-label {
  font-size: 0.9rem;
  font-weight: 500;
}

@media (max-width: 768px) {
  .hero-title {
    font-size: 2rem;
  }

  .hero-subtitle {
    font-size: 1.1rem;
  }

  .format-icons-grid {
    gap: 2rem;
  }

  .format-icon {
    font-size: 2.5rem;
  }

  .features-grid {
    gap: 1rem;
  }

  .feature-item {
    padding: 0.5rem 1rem;
  }

  .feature-label {
    font-size: 0.8rem;
  }
}

@media (max-width: 480px) {
  .hero-title {
    font-size: 1.5rem;
  }

  .format-icons-grid {
    gap: 1.5rem;
  }

  .features-grid {
    flex-direction: column;
    align-items: center;
  }
}
</style>
